import type { IconName } from './ui/Icon'

/**
 * Every page, once.
 *
 * The navigation, the page titles and the help dialog all read this, so a page
 * is called the same thing everywhere and none of them can offer a page the
 * others do not know about.
 */

export const PRODUCT_NAME = 'DashVERSE-LW'
export const PRODUCT_TAGLINE = 'Research software quality dashboard'

/** What a page needs before it has anything to show. */
type Needs = 'nothing' | 'no-data' | 'data' | 'two-software'

export interface Page {
  path: string
  label: string
  section: 'Dashboard' | 'Preferences'
  icon: IconName
  needs: Needs
  /**
   * Addresses this page used to have. Each redirects here, query included, so
   * old links and bookmarks keep working.
   */
  aliases?: string[]
}

export const PAGES: Page[] = [
  {
    path: '/welcome',
    label: 'Welcome',
    section: 'Dashboard',
    icon: 'home',
    needs: 'no-data',
    aliases: ['/start', '/home'],
  },
  {
    path: '/assessment',
    label: 'Assessment',
    section: 'Dashboard',
    icon: 'document',
    needs: 'data',
    aliases: [
      '/summary',
      '/details',
      '/scorecard',
      '/inspect',
      '/evidence',
      '/diagnostics',
      '/overview',
      '/projects',
      '/compare',
      '/indicators',
      '/coverage',
      '/data-quality',
      '/data',
      '/sources',
      '/publishing',
      '/concepts',
      '/privacy',
      '/about',
      '/benchmark',
      '/catalogue',
      '/quality',
      '/reports',
      '/assessments',
      '/find',
      '/browse',
      '/scan',
      '/assess',
      '/integrate',
      '/accessibility',
      '/credits',
    ],
  },
  {
    path: '/settings',
    label: 'Settings',
    section: 'Preferences',
    icon: 'settings',
    needs: 'nothing',
  },
]

export const SECTIONS = [...new Set(PAGES.map((page) => page.section))]

/**
 * Every address that used to lead somewhere, and where it leads now: each
 * page's aliases, and the pages that became a section of another.
 */
export const REDIRECTS: Record<string, string> = {
  ...Object.fromEntries(
    PAGES.flatMap((page) => (page.aliases ?? []).map((alias) => [alias, page.path])),
  ),
  '/matrix': '/assessment',
  '/compare/matrix': '/assessment',
  '/compare-matrix': '/assessment',
  '/data/sources': '/assessment',
}

/** Whether a page has something to show for this session. */
export function isAvailable(page: Page, reportCount: number, _softwareCount?: number): boolean {
  if (page.needs === 'no-data') return reportCount === 0
  if (page.needs === 'data') return reportCount > 0
  return true
}

export function pageFor(pathname: string): Page | undefined {
  const path = pathname.toLowerCase()
  if (path.startsWith('/assessment/')) return undefined
  const matches = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`)
  return PAGES.find((page) => [page.path, ...(page.aliases ?? [])].some(matches))
}

/** Where to see one assessment, or the latest of one piece of software. */
export function assessmentHref(softwareKey: string, runId?: string): string {
  const params = new URLSearchParams({ software: softwareKey })
  if (runId) params.set('run', runId)
  return `/assessment?${params}`
}
